import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import {
  Rock,
  Issue,
  Metric,
  User,
  Milestone,
  ActionItem,
  COLLECTION_NAMES,
} from '../types';
import {
  calculateMilestoneProgress,
  getRockIssues,
} from './dashboard.util';
import type {
  RockWithUsers,
  RockWithDetails,
} from './dashboard.types';

/**
 * Hook for Dashboard data with Firestore
 * Provides statistics and preview data for the main EOS dashboard
 */
export function useDashboard(userId: string | undefined) {
  const [rocks, setRocks] = useState<Rock[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const { eosUsers = [] } = useAuth() as { eosUsers: User[] };
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [rockActionItems, setRockActionItems] = useState<ActionItem[]>([]);
  const [companyIssues, setCompanyIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);

  // Load active rocks (not achieved, not dropped)
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const rocksQuery = query(
      collection(db, COLLECTION_NAMES.ROCKS),
      where('isAchieved', '==', false),
      where('isDropped', '==', false),
      orderBy('dueDate', 'asc')
    );

    const unsubscribe = onSnapshot(
      rocksQuery,
      (snapshot) => {
        const rocksData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            createdAt: data.createdAt || data.createdAt,
            updatedAt: data.updatedAt || data.updatedAt,
          } as Rock;
        });
        setRocks(rocksData);
      },
      (error) => {
        console.error('Error loading rocks:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load open issues (filter out solved in memory to avoid index requirement)
  useEffect(() => {
    if (!userId) {
      return;
    }

    const issuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      issuesQuery,
      (snapshot) => {
        const issuesData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || data.createdAt,
              updatedAt: data.updatedAt || data.updatedAt,
              firstOpenedDate: data.firstOpenedDate || data.createdAt,
            } as Issue;
          })
          .filter((issue) => issue.status !== 'solved'); // Filter out solved issues
        setIssues(issuesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading issues:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load active metrics (not deleted)
  useEffect(() => {
    if (!userId) {
      return;
    }

    const metricsQuery = query(
      collection(db, COLLECTION_NAMES.METRICS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      metricsQuery,
      (snapshot) => {
        const metricsData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            // Filter out deleted metrics
            if (data.deletedAt) return null;
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || data.createdAt,
              updatedAt: data.updatedAt || data.updatedAt,
            } as Metric;
          })
          .filter((metric) => metric !== null) as Metric[];
        setMetrics(metricsData);
      },
      (error) => {
        console.error('Error loading metrics:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Load milestones for all rocks (with batching for Firestore 'in' query limit)
  const loadMilestonesForRocks = useCallback(async (rockIds: string[]): Promise<Map<string, Milestone[]>> => {
    const milestonesMap = new Map<string, Milestone[]>();

    if (rockIds.length === 0) return milestonesMap;

    // Firestore 'in' queries are limited to 10 items, so we need to batch
    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.map(async (batch) => {
        const milestonesQuery = query(
          collection(db, COLLECTION_NAMES.MILESTONES),
          where('rockId', 'in', batch),
          orderBy('dueDate', 'asc')
        );
        const snapshot = await getDocs(milestonesQuery);
        return snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            completedAt: data.completedAt?.toDate?.() || data.completedAt,
            createdAt: data.createdAt || Timestamp.now(),
          } as Milestone;
        });
      });

      const results = await Promise.all(promises);
      const allMilestones = results.flat();

      // Group milestones by rockId
      allMilestones.forEach((milestone) => {
        if (!milestonesMap.has(milestone.rockId)) {
          milestonesMap.set(milestone.rockId, []);
        }
        milestonesMap.get(milestone.rockId)!.push(milestone);
      });
    } catch (error) {
      console.error('Error loading milestones:', error);
    }

    return milestonesMap;
  }, []);

  // Load action items for filtered rocks (with batching) - returns Map organized by rockId
  const loadActionItemsForRocks = useCallback(async (rockIds: string[]): Promise<Map<string, ActionItem[]>> => {
    const actionItemsMap = new Map<string, ActionItem[]>();

    if (rockIds.length === 0) return actionItemsMap;

    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.flatMap(async (batch) => {
        // Query for both rock-specific (rockId) and linked (linkedRockId) action items
        const [rockSpecificQuery, linkedQuery] = await Promise.all([
          getDocs(query(
            collection(db, COLLECTION_NAMES.ACTION_ITEMS),
            where('rockId', 'in', batch),
            where('dropped', '==', false)
          )),
          getDocs(query(
            collection(db, COLLECTION_NAMES.ACTION_ITEMS),
            where('linkedRockId', 'in', batch),
            where('dropped', '==', false)
          ))
        ]);
        
        const allDocs = [...rockSpecificQuery.docs, ...linkedQuery.docs];
        return allDocs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure description exists (use task as fallback for legacy data)
            description: data.description || data.task || '',
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as ActionItem;
        });
      });

      const results = await Promise.all(promises);
      const allActionItems = results.flat();
      
      // Remove duplicates and organize by rockId
      const uniqueActionItems = Array.from(
        new Map(allActionItems.map(item => [item.id, item])).values()
      );
      
      // Organize by rockId (either rockId or linkedRockId)
      uniqueActionItems.forEach((actionItem) => {
        const rockId = actionItem.rockId || actionItem.linkedRockId;
        if (rockId) {
          if (!actionItemsMap.has(rockId)) {
            actionItemsMap.set(rockId, []);
          }
          actionItemsMap.get(rockId)!.push(actionItem);
        }
      });
      
      return actionItemsMap;
    } catch (error) {
      console.error('Error loading action items:', error);
      return actionItemsMap;
    }
  }, []);

  // Load rock action items for rocks (with batching)
  const loadRockActionItemsForRocks = useCallback(async (rockIds: string[]): Promise<Map<string, ActionItem[]>> => {
    const actionItemsMap = new Map<string, ActionItem[]>();

    if (rockIds.length === 0) return actionItemsMap;

    const batchSize = 10;
    const batches: string[][] = [];

    for (let i = 0; i < rockIds.length; i += batchSize) {
      batches.push(rockIds.slice(i, i + batchSize));
    }

    try {
      const promises = batches.map(async (batch) => {
        const actionItemsQuery = query(
          collection(db, COLLECTION_NAMES.ACTION_ITEMS),
          where('rockId', 'in', batch)
        );
        const snapshot = await getDocs(actionItemsQuery);
        return snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: data.dueDate?.toDate?.() || data.dueDate,
            createdAt: data.createdAt || Timestamp.now(),
          } as ActionItem;
        });
      });

      const results = await Promise.all(promises);
      const allActionItems = results.flat();

      // Group action items by rockId
      allActionItems.forEach((actionItem) => {
        if (!actionItemsMap.has(actionItem.rockId)) {
          actionItemsMap.set(actionItem.rockId, []);
        }
        actionItemsMap.get(actionItem.rockId)!.push(actionItem);
      });
    } catch (error) {
      console.error('Error loading action items:', error);
    }

    return actionItemsMap;
  }, []);

  // Load company issues (isRock=false, status≠solved)
  useEffect(() => {
    if (!userId) {
      return;
    }

    const companyIssuesQuery = query(
      collection(db, COLLECTION_NAMES.ISSUES),
      where('isRock', '==', false),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      companyIssuesQuery,
      (snapshot) => {
        const issuesData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt || Timestamp.now(),
              updatedAt: data.updatedAt || Timestamp.now(),
              firstOpenedDate: data.firstOpenedDate || data.createdAt,
              solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
            } as Issue;
          })
          .filter((issue) => issue.status !== 'solved' && !issue.solvedAt); // Filter out solved issues
        setCompanyIssues(issuesData);
      },
      (error) => {
        console.error('Error loading company issues:', error);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Filter rocks by user, department, and collaborator toggle
  const getFilteredRocks = useCallback((
    selectedUserId: string | 'all',
    selectedDepartment: string | 'all',
    showCollaboratorRocks: boolean,
    currentUserId?: string
  ): Rock[] => {
    let filtered = rocks;

    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter((rock) => rock.departmentId === selectedDepartment);
    }

    // Apply user filter
    if (selectedUserId === 'all') {
      return filtered;
    }

    if (showCollaboratorRocks && selectedUserId === currentUserId) {
      // Show rocks where user is collaborator
      return filtered.filter((rock) => rock.collaborators?.includes(selectedUserId));
    }

    // Show rocks owned by user
    return filtered.filter((rock) => rock.ownerId === selectedUserId);
  }, [rocks]);

  // Filter company issues by user and department
  const getFilteredCompanyIssues = useCallback((
    selectedUserId: string | 'all',
    selectedDepartment: string | 'all'
  ): Issue[] => {
    let filtered = companyIssues;

    if (selectedUserId !== 'all') {
      filtered = filtered.filter((issue) => issue.assignedToUserId === selectedUserId);
    }

    if (selectedDepartment !== 'all') {
      filtered = filtered.filter((issue) => issue.departmentId === selectedDepartment);
    }

    return filtered;
  }, [companyIssues]);

  // Filter data by selected user (legacy function for compatibility)
  const getFilteredData = (selectedUserId: string | 'all') => {
    if (selectedUserId === 'all') {
      return { rocks, issues, metrics };
    }

    return {
      rocks: rocks.filter(
        (rock) => rock.ownerId === selectedUserId || rock.collaborators?.includes(selectedUserId)
      ),
      issues: issues.filter((issue) => issue.ownerId === selectedUserId),
      metrics: metrics.filter((metric) => metric.ownerId === selectedUserId),
    };
  };

  // Get user's rocks (for "My Rocks" preview)
  const getMyRocks = (userId: string) => {
    return rocks
      .filter(
        (rock) => rock.ownerId === userId || rock.collaborators?.includes(userId)
      )
      .slice(0, 5)
      .sort((a, b) => {
        // Sort by status priority: off-track > on-track
        const statusPriority = { 'off-track': 2, 'on-track': 1 };
        return (statusPriority[b.status] || 0) - (statusPriority[a.status] || 0);
      });
  };

  // Get recent issues (for preview)
  const getRecentIssues = (limitCount: number = 5) => {
    return issues.slice(0, limitCount);
  };

  // Calculate rock progress from milestones
  const calculateRockProgress = useCallback((rockId: string, allMilestones: Milestone[]): number => {
    const rockMilestones = allMilestones.filter((m) => m.rockId === rockId);
    if (rockMilestones.length === 0) return 0;
    return calculateMilestoneProgress(rockMilestones);
  }, []);

  // Get overdue milestones
  const getOverdueMilestones = useCallback((allMilestones: Milestone[]): Milestone[] => {
    const incomplete = allMilestones.filter((m) => !m.complete);
    return incomplete.filter((m) => {
      if (!m.dueDate) return false;
      const dueDate = m.dueDate instanceof Date ? m.dueDate : new Date(m.dueDate);
      return dueDate < new Date();
    });
  }, []);

  // Get overdue action items
  const getOverdueActionItems = useCallback((allActionItems: ActionItem[]): ActionItem[] => {
    const incomplete = allActionItems.filter((t) => !t.complete);
    return incomplete.filter((t) => {
      if (!t.dueDate) return false;
      const dueDate = t.dueDate instanceof Date ? t.dueDate : new Date(t.dueDate);
      return dueDate < new Date();
    });
  }, []);

  // Calculate enhanced statistics
  const getStats = useCallback((
    selectedUserId: string | 'all',
    selectedDepartment: string | 'all',
    showCollaboratorRocks: boolean,
    currentUserId?: string,
    allMilestones: Milestone[] = [],
    allActionItems: ActionItem[] = []
  ) => {
    const filteredRocks = getFilteredRocks(selectedUserId, selectedDepartment, showCollaboratorRocks, currentUserId);
    const filteredCompanyIssues = getFilteredCompanyIssues(selectedUserId, selectedDepartment);

    // Calculate progress for filtered rocks
    let totalProgress = 0;
    filteredRocks.forEach((rock) => {
      totalProgress += calculateRockProgress(rock.id, allMilestones);
    });
    const avgProgress = filteredRocks.length > 0 ? Math.round(totalProgress / filteredRocks.length) : 0;

    // Get user-specific milestones and action items for overdue calculation
    const userMilestones = selectedUserId !== 'all'
      ? allMilestones.filter((m) => {
          const rock = filteredRocks.find((r) => r.id === m.rockId);
          return rock !== undefined;
        })
      : [];
    const userActionItems = selectedUserId !== 'all'
      ? allActionItems.filter((t) => {
          const rock = filteredRocks.find((r) => r.id === t.linkedRockId);
          return rock !== undefined;
        })
      : [];

    const overdueMilestones = getOverdueMilestones(userMilestones);
    const overdueActionItems = getOverdueActionItems(userActionItems);

    return {
      total: filteredRocks.length,
      onTrack: filteredRocks.filter((r) => r.status === 'on-track').length,
      offTrack: filteredRocks.filter((r) => r.status === 'off-track').length,
      avgProgress,
      milestones: {
        total: userMilestones.filter((m) => !m.complete).length,
        overdue: overdueMilestones.length,
      },
      actionItems: {
        total: userActionItems.filter((t) => !t.complete).length,
        overdue: overdueActionItems.length,
      },
      companyIssues: {
        total: filteredCompanyIssues.length,
      },
    };
  }, [getFilteredRocks, getFilteredCompanyIssues, calculateRockProgress, getOverdueMilestones, getOverdueActionItems]);

  /**
   * Set up real-time subscription for issues (table view only)
   * This function should be called from the component when table view is active
   * @param rockIds Array of rock IDs to monitor
   * @param onUpdate Callback function when issues change
   * @returns Unsubscribe function
   */
  const subscribeToIssues = useCallback(
    (rockIds: string[], onUpdate: (updatedIssues: Issue[]) => void) => {
      if (rockIds.length === 0) {
        return () => {}; // No-op unsubscribe
      }

      // Firestore doesn't support 'in' with >10 items, so we subscribe to all rock issues
      // and filter client-side
      const issuesQuery = query(
        collection(db, COLLECTION_NAMES.ISSUES),
        where('isRock', '==', true),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        issuesQuery,
        (snapshot) => {
          const issuesData = snapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt || Timestamp.now(),
                updatedAt: data.updatedAt || Timestamp.now(),
                firstOpenedDate: data.firstOpenedDate || data.createdAt,
                solvedAt: data.solvedAt?.toDate?.() || data.solvedAt,
              } as Issue;
            })
            .filter(
              (issue) =>
                rockIds.includes(issue.relatedRockId || '') && issue.status !== 'solved'
            );
          onUpdate(issuesData);
        },
        (error) => {
          console.error('Error in real-time issues subscription:', error);
        }
      );

      return unsubscribe;
    },
    []
  );

  return {
    rocks,
    issues,
    metrics,
    users: eosUsers,
    milestones,
    actionItems,
    companyIssues,
    loading,
    getFilteredData,
    getFilteredRocks,
    getFilteredCompanyIssues,
    getMyRocks,
    getRecentIssues,
    getStats,
    loadMilestonesForRocks,
    loadActionItemsForRocks,
    loadRockActionItemsForRocks,
    calculateRockProgress,
    getOverdueMilestones,
    getOverdueActionItems,
    getRockIssues: (rockId: string) => getRockIssues(rockId, issues),
    subscribeToIssues,
  };
}

